import type { PlaceCoords } from '@bandido/shared/types';

import { catalogEntryCoords, unambiguousCatalogCoords } from './catalogCoords';
import {
  BusesSearchPayload,
  BusStationsCatalogPage,
  CatalogEntry,
  PricedSearchResult,
} from './queries';
import { routeMapEndpoints } from './routeMap';
import type { RouteMapPoint } from './routeMap';
import { captureSearchQuote, quoteSearchIdentity, SelectedTravelQuote } from './selectedQuote';

/**
 * Helpers del vertical de buses (micros de media/larga distancia).
 *
 * Cada oferta es un servicio EN UNA CALIDAD concreta (Semi-cama, Cama...): el
 * mismo micro llega varias veces, una por calidad, cada una con su precio y su
 * provenance. Las calidades hermanas vienen en `otherSeatClasses` para
 * mostrarlas sin re-buscar.
 *
 * El precio de búsqueda es la tarifa PELADA del asiento: los impuestos de
 * venta aparecen recién en el hold (`priceIsIndicative`, misma semántica que
 * circuits — search indica, hold promete).
 */

export type BusOffer = PricedSearchResult;

export interface BusQuoteGeneration {
  payload: BusesSearchPayload | null;
  quotedAt: number | undefined;
}

export interface BusQuoteChain {
  mainSearchIdentity?: string;
  outboundQuote: SelectedTravelQuote<BusOffer> | null;
  bookingQuote: SelectedTravelQuote<BusOffer> | null;
  outboundExpired: boolean;
}

export function emptyBusQuoteChain(outboundExpired = false, mainSearchIdentity?: string): BusQuoteChain {
  return {
    ...(mainSearchIdentity ? { mainSearchIdentity } : {}),
    outboundQuote: null,
    bookingQuote: null,
    outboundExpired,
  };
}

export function captureOutboundBusQuote(
  offer: BusOffer,
  agencyId: string | number,
  mainGeneration: BusQuoteGeneration,
): SelectedTravelQuote<BusOffer> | null {
  return captureSearchQuote(offer, mainGeneration.quotedAt, 'buses', agencyId, mainGeneration.payload);
}

export function busReturnPayload(outboundQuote: SelectedTravelQuote<BusOffer> | null): BusesSearchPayload | null {
  return outboundQuote ? { outboundOfferRef: outboundQuote.offer.provenance } : null;
}

/** A new outbound owns a new return chain and cannot retain an old final quote. */
export function busChainWithOutbound(outboundQuote: SelectedTravelQuote<BusOffer>): BusQuoteChain {
  return {
    mainSearchIdentity: outboundQuote.searchIdentity,
    outboundQuote,
    bookingQuote: null,
    outboundExpired: false,
  };
}

export function busChainWithFinalQuote(
  chain: BusQuoteChain,
  bookingQuote: SelectedTravelQuote<BusOffer>,
): BusQuoteChain {
  return {
    ...chain,
    mainSearchIdentity: chain.mainSearchIdentity ?? bookingQuote.searchIdentity,
    bookingQuote,
    outboundExpired: false,
  };
}

/**
 * Browser/history or committed main-search changes invalidate every downstream
 * generation. Same-key refreshes keep the semantic identity and preserve P1.
 */
export function reconcileBusChainWithMainSearch(
  chain: BusQuoteChain,
  currentMainSearchIdentity: string | undefined,
): BusQuoteChain {
  if (chain.mainSearchIdentity && chain.mainSearchIdentity !== currentMainSearchIdentity) {
    return emptyBusQuoteChain();
  }
  if (chain.outboundQuote) {
    return chain.outboundQuote.searchIdentity === currentMainSearchIdentity ? chain : emptyBusQuoteChain();
  }
  if (chain.bookingQuote && chain.bookingQuote.searchIdentity !== currentMainSearchIdentity) {
    return emptyBusQuoteChain();
  }
  return chain;
}

export function mainBusSearchIdentity(
  agencyId: string | number,
  payload: BusesSearchPayload | null,
): string | undefined {
  return payload ? quoteSearchIdentity('buses', agencyId, payload) : undefined;
}

/** Final one-way offers use main; final round-trip offers use return generation. */
export function captureFinalBusQuote(
  offer: BusOffer,
  agencyId: string | number,
  mainGeneration: BusQuoteGeneration,
  returnGeneration?: BusQuoteGeneration,
): SelectedTravelQuote<BusOffer> | null {
  const generation = returnGeneration ?? mainGeneration;
  return captureSearchQuote(
    offer,
    generation.quotedAt,
    'buses',
    agencyId,
    generation.payload,
  );
}

/** "2026-08-14T22:00" → "22:00". Hora LOCAL de la parada, sin zona horaria. */
export function busTime(value?: string): string {
  if (!value) return '';
  const [, time] = value.split('T');
  return time ? time.slice(0, 5) : '';
}

/** "2026-08-14T22:00" → "14/08". El día importa: muchos servicios son nocturnos. */
export function busDay(value?: string): string {
  if (!value) return '';
  const [date] = value.split('T');
  const [, month, day] = (date ?? '').split('-');
  return day ? `${day}/${month}` : '';
}

/** ¿Llega otro día del que salió? Se dice: un nocturno que no lo aclara confunde. */
export function arrivesNextDay(offer: BusOffer): boolean {
  const [from] = (offer.departure ?? '').split('T');
  const [to] = (offer.arrival ?? '').split('T');
  return Boolean(from && to && from !== to);
}

/**
 * Directo = UN tramo. No es lo mismo que "sin paradas": un directo puede parar
 * (`stopCount`) sin que el pasajero cambie de unidad.
 */
export function isDirect(offer: BusOffer): boolean {
  return (offer.segments?.length ?? 1) <= 1;
}

/**
 * Por debajo de esto quedan pocos asientos y vale avisarlo. Más arriba,
 * "Quedan 47" no es una señal de nada: es ruido con color de alerta.
 */
export const FEW_SEATS_THRESHOLD = 8;

/**
 * ¿Avisar que quedan pocos asientos? Solo con el dato REAL del proveedor: sin
 * `freeSeats` no se infiere escasez, porque "no lo publica" y "quedan pocos"
 * no son lo mismo y la diferencia la paga el pasajero apurando una compra.
 */
export function hasFewSeats(offer: BusOffer): boolean {
  return typeof offer.freeSeats === 'number' && offer.freeSeats <= FEW_SEATS_THRESHOLD;
}

/** La calidad de la oferta, con respaldo cuando el proveedor no la rotula. */
export function seatClassName(offer: BusOffer): string {
  return offer.seatClass?.name ?? offer.seatClass?.code ?? '';
}

export interface BusFilters {
  /** Nombres de calidad (Semi-cama, Cama...). */
  classes: string[];
  operators: string[];
  directOnly: boolean;
}

export const EMPTY_BUS_FILTERS: BusFilters = {
  classes: [],
  operators: [],
  directOnly: false,
};

export function filterBuses(offers: BusOffer[], filters: BusFilters): BusOffer[] {
  return offers.filter((offer) => {
    if (filters.classes.length && !filters.classes.includes(seatClassName(offer))) {
      return false;
    }
    if (
      filters.operators.length &&
      !filters.operators.includes(String(offer.operator ?? ''))
    ) {
      return false;
    }
    if (filters.directOnly && !isDirect(offer)) return false;
    return true;
  });
}

/** Cuántas ofertas quedarían con ESTA opción puesta (búsqueda facetada). */
export function busFacetCount(
  offers: BusOffer[],
  filters: BusFilters,
  patch: Partial<BusFilters>,
): number {
  return filterBuses(offers, { ...filters, ...patch }).length;
}

export type BusSort = 'departure' | 'price' | 'duration';

/**
 * Por horario de salida por defecto: en un micro se elige CUÁNDO viajar; el
 * precio desempata entre calidades del mismo servicio.
 */
export function sortBuses(offers: BusOffer[], sort: BusSort): BusOffer[] {
  const copy = [...offers];
  if (sort === 'price') {
    return copy.sort(
      (a, b) => a.pricing.final.amountMinor - b.pricing.final.amountMinor,
    );
  }
  if (sort === 'duration') {
    return copy.sort(
      (a, b) =>
        (a.durationMinutes ?? Number.MAX_SAFE_INTEGER) -
        (b.durationMinutes ?? Number.MAX_SAFE_INTEGER),
    );
  }
  return copy.sort((a, b) =>
    (a.departure ?? '').localeCompare(b.departure ?? ''));
}

// ── Mapa del recorrido: resolución de las paradas comiteadas (#2255) ───────

/**
 * Coordenadas de la parada COMITEADA, o `undefined`.
 *
 * La referencia que viaja en la búsqueda es el CÓDIGO de la parada (`id` del
 * catálogo, "RET"), y se resuelve por él —nunca por el nombre: el `q` con el
 * que se pidió el catálogo filtra por nombre y trae vecinas ("Retiro",
 * "Retiro (La Noria)").
 *
 * **Manda el catálogo, no la selección viva.** Si el catálogo tiene algo que
 * decir sobre ese código, decide él: el código es del PROVEEDOR y la búsqueda
 * puede abrirse a varios, así que dos entradas del mismo código con
 * coordenadas distintas son una ambigüedad real y ahí no se dibuja ninguna.
 * Que el asesor haya elegido una de esas filas no prueba que todos los
 * proveedores llamen igual al mismo lugar, y un punto plausible pero
 * equivocado es indistinguible del correcto para él.
 *
 * **Y solo si la lista está COMPLETA**, probado con el acuse de proveedores y
 * el `total` del envelope. Un proveedor que no contestó, o una lista que trajo
 * menos de las que matchean (el hub filtra por nombre "contiene" y no promete
 * ningún orden), dejan afuera candidatos: el que discrepa puede ser justo uno
 * de ellos, y la "coincidencia única" sería un artefacto de lo que no vino.
 * Una página que no se puede probar completa no resuelve nada — ni por el
 * catálogo ni por el atajo, porque el atajo tampoco sabe de la entrada que
 * falta.
 *
 * La entrada VIVA del formulario queda como ATAJO para cuando el catálogo
 * todavía no dijo nada de ese código —la respuesta no llegó, o vino completa y
 * ese código no está—: dibuja el mapa sin esperar el roundtrip. Vale solo si
 * sigue siendo la comiteada; si el asesor cambió la parada después de buscar,
 * lo que se dibuja nunca es la parada nueva, y con catálogo cargado sigue
 * siendo la del PEDIDO.
 *
 * Hoy el proveedor no publica `lat`/`lng` en ese catálogo (el contrato del hub
 * lo dice explícito), así que esto devuelve `undefined` SIEMPRE y no hay card
 * — el comportamiento que ya había. El día que las publique (rutero#2230), el
 * mapa aparece solo: las coordenadas son campos declarados del schema
 * genérico de entrada de catálogo, no un campo nuevo que haya que agregar acá.
 */
function selectionCoords(
  committed: string,
  selection: CatalogEntry | null,
): PlaceCoords | undefined {
  return selection?.id === committed ? catalogEntryCoords(selection) : undefined;
}

/**
 * ¿Se vieron TODOS los candidatos? Hay que descartar las dos formas de faltar:
 *
 * 1. Que un proveedor del fan-out no haya contestado (`failed`/`timedOut` del
 *    envelope): sus paradas no están, y una de ellas puede traer el mismo
 *    código con otra coordenada. `undefined` es "no se pudo leer el acuse", que
 *    no es lo mismo que cero: también falla cerrado.
 * 2. Que hayan venido menos entradas de las que matchean. Eso lo dice el
 *    `total` del envelope y NADA MÁS: el `limit` que se pidió es un máximo, no
 *    una promesa —una respuesta corta con un `total` mayor es exactamente el
 *    caso que hay que rechazar—. Un `total` que no es un entero no negativo es
 *    metadato ilegible y también falla cerrado.
 */
function catalogPageIsComplete(page: BusStationsCatalogPage): boolean {
  if (page.missingProviders !== 0) return false;
  const { total } = page;
  return (
    typeof total === 'number'
    && Number.isInteger(total)
    && total >= 0
    && total <= page.results.length
  );
}

export function busStationCoords(
  committed: string,
  selection: CatalogEntry | null,
  catalog: BusStationsCatalogPage | undefined,
): PlaceCoords | undefined {
  // Todavía sin respuesta: el atajo dibuja sin esperar el roundtrip.
  if (!catalog) return selectionCoords(committed, selection);
  // Con una página que no se puede probar completa no se afirma NADA: la
  // entrada que discrepa puede ser la que no vino.
  if (!catalogPageIsComplete(catalog)) return undefined;

  const candidates = catalog.results.filter((entry) => entry.id === committed);
  // Con evidencia del catálogo, el veredicto es el suyo — incluido el
  // `undefined` de la ambigüedad: falla cerrado, no se cae al atajo.
  if (candidates.length > 0) return unambiguousCatalogCoords(candidates);

  return selectionCoords(committed, selection);
}

/** Un extremo del micro pedido: cómo se resuelve y cómo se rotula. */
export interface BusStationEndpointSources {
  /** La parada elegida en el picker, siga o no siendo la comiteada. */
  selection: CatalogEntry | null;
  /**
   * La página del catálogo pedida con el NOMBRE comiteado de la parada (el hub
   * filtra por nombre, no por código). Es la PÁGINA y no las filas porque
   * resolver exige saber si se vieron todos los candidatos. Sin nombre en la
   * URL no hay con qué consultarlo y queda el atajo de la selección viva.
   */
  catalog?: BusStationsCatalogPage;
  /**
   * Nombre para mostrar, el comiteado en la URL: la referencia del pedido es
   * el código ("RET"), que no es un rótulo. Sin él la card queda con el
   * código, que es lo que el pedido tiene.
   */
  label?: string;
}

/**
 * Origen y destino del micro pedido, para el mapa del recorrido.
 *
 * Sale del PEDIDO, no del formulario vivo: si el asesor cambió la parada
 * después de buscar, el mapa sigue siendo el de la búsqueda que produjo la
 * oferta — o no hay mapa, nunca el de otra ruta.
 *
 * En el ida y vuelta los dos tramos unen las MISMAS dos paradas y se reservan
 * en un solo booking, así que el par comiteado describe el recorrido en los
 * dos pasos: no se invierte al elegir la vuelta.
 */
export function busRouteMapPoints(
  payload: BusesSearchPayload | null,
  from: BusStationEndpointSources,
  to: BusStationEndpointSources,
): RouteMapPoint[] {
  if (!payload?.fromStation || !payload.toStation) return [];
  const endpoint = (
    committed: string,
    sources: BusStationEndpointSources,
  ) => ({
    label: sources.label ?? committed,
    coords: busStationCoords(committed, sources.selection, sources.catalog),
  });
  return routeMapEndpoints(
    endpoint(payload.fromStation, from),
    endpoint(payload.toStation, to),
  );
}

/** Rótulos traducidos que el resumen del modal necesita, resueltos por la UI. */
export interface BusBookingDetailLabels {
  outbound: string;
  return: string;
  pax: string;
}

/**
 * El resumen del viaje que ve el modal de reserva.
 *
 * Ida y vuelta de buses es UNA reserva: la provenance de la vuelta encadena
 * los dos tramos y el hold los cubre a los dos (§5 de docs/RUTERO.md). Por eso
 * el resumen los muestra JUNTOS — el asesor está por reservar los dos, y ver
 * solo el de vuelta (que es la oferta que quedó seleccionada) es leer la mitad
 * de lo que se está comprando.
 *
 * Sin ida encadenada —un one-way, o una ida que dejó de pertenecer a la
 * búsqueda vigente— queda el tramo único, sin renglón fantasma.
 */
export function busBookingDetailLines(
  booking: BusOffer | null,
  outbound: BusOffer | null,
  passengers: number,
  labels: BusBookingDetailLabels,
): string[] {
  if (!booking) return [];

  const chained = booking.leg === 'return' && outbound ? outbound : null;
  const leg = (offer: BusOffer, label?: string) =>
    [
      label,
      [offer.fromStation, offer.toStation].filter(Boolean).join(' → '),
      `${busDay(offer.departure)} ${busTime(offer.departure)}`.trim(),
      seatClassName(offer),
    ]
      .filter(Boolean)
      .join(' · ');

  return [
    ...(chained ? [leg(chained, labels.outbound), leg(booking, labels.return)] : [leg(booking)]),
    `${labels.pax}: ${passengers}`,
  ];
}
